<template>
  <div
    class="modal fade"
    id="supplierCreateModal"
    tabindex="-1"
    aria-labelledby="supplierCreateModalLabel"
    aria-hidden="true"
  >
      <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ supplier ? $t("supplier_page.edit_supplier_chip") : $t("supplier_page.new_supplier") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1" id="supplierCreateModalLabel">
              {{ supplier ? $t("supplier_page.update_supplier") : $t("supplier_page.create_supplier") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("supplier_page.modal_description") }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close shadow-none"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          ></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <form @submit.prevent="handleSubmit">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.supplier_code") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.supplier_code_caption") }}</span>
                </div>
                <div class="reference-input-wrap">
                  <input
                    v-model="formData.code"
                    type="text"
                    class="form-control modal-input"
                    :class="{ 'is-invalid': fieldErrors.code }"
                    :placeholder="$t('supplier_page.supplier_code_placeholder')"
                    required
                  />
                  <button
                    v-if="!supplier"
                    type="button"
                    class="btn btn-light generate-code-btn"
                    :disabled="loading"
                    @click="generateSupplierCode"
                  >
                    <i class="ri-refresh-line me-1"></i>
                    {{ $t("supplier_page.generate") }}
                  </button>
                </div>
                <div v-if="fieldErrors.code" class="invalid-feedback">{{ fieldErrors.code }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.supplier_name") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.supplier_name_caption") }}</span>
                </div>
                <input
                  v-model="formData.name"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.name }"
                  :placeholder="$t('supplier_page.supplier_name_placeholder')"
                  required
                />
                <div v-if="fieldErrors.name" class="invalid-feedback">{{ fieldErrors.name }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.email") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.email_caption") }}</span>
                </div>
                <input
                  v-model="formData.email"
                  type="email"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.email }"
                  :placeholder="$t('supplier_page.email_placeholder')"
                />
                <div v-if="fieldErrors.email" class="invalid-feedback">{{ fieldErrors.email }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.phone") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.phone_caption") }}</span>
                </div>
                <input
                  v-model="formData.phone"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.phone }"
                  :placeholder="$t('supplier_page.phone_placeholder')"
                />
                <div v-if="fieldErrors.phone" class="invalid-feedback">{{ fieldErrors.phone }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.country") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.country_caption") }}</span>
                </div>
                <input
                  v-model="formData.country"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('supplier_page.country_placeholder')"
                />
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.company_name") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.company_name_caption") }}</span>
                </div>
                <input
                  v-model="formData.com_name"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('supplier_page.company_name_placeholder')"
                />
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.status") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.status_caption") }}</span>
                </div>
                <select v-model="formData.status" class="form-select modal-input">
                  <option :value="1">{{ $t("supplier_page.status_active") }}</option>
                  <option :value="0">{{ $t("supplier_page.status_inactive") }}</option>
                </select>
              </div>

              <div class="col-12">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("supplier_page.address") }}</label>
                  <span class="field-caption">{{ $t("supplier_page.address_caption") }}</span>
                </div>
                <textarea
                  v-model="formData.address"
                  rows="4"
                  class="form-control modal-input modal-textarea"
                  :placeholder="$t('supplier_page.address_placeholder')"
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
              <button type="button" class="btn btn-light px-4" data-bs-dismiss="modal">
                {{ $t("common.cancel") }}
              </button>
              <button type="submit" class="btn style-one px-4 h-55" :disabled="loading">
                {{ loading ? $t("supplier_page.saving") : supplier ? $t("supplier_page.update_supplier") : $t("supplier_page.create_supplier") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import supplierService, { Supplier } from "../../../services/supplier";
import stateStore from "@/utils/store";

export default defineComponent({
  name: "CreateSupplierForm",
  props: {
    supplier: {
      type: Object as PropType<Supplier | null>,
      default: null,
    },
  },
  emits: ["saved"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const loading = ref(false);
    const fieldErrors = reactive<Record<string, string>>({});
    const formData = reactive<Supplier>({
      name: "",
      email: "",
      phone: "",
      address: "",
      country: "",
      com_name: "",
      status: 1,
      code: "",
    });

    const resetForm = () => {
      formData.id = undefined;
      formData.name = "";
      formData.email = "";
      formData.phone = "";
      formData.address = "";
      formData.country = "";
      formData.com_name = "";
      formData.status = 1;
      formData.code = "";
    };

    watch(
      () => props.supplier,
      (newSupplier) => {
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

        if (newSupplier) {
          Object.assign(formData, {
            ...newSupplier,
            status: newSupplier.status ?? 1,
          });
          return;
        }

        resetForm();
      },
      { immediate: true }
    );

    const hideModal = () => {
      const modalElement = document.getElementById("supplierCreateModal");
      if (!modalElement) return;

      const dismissButton = modalElement.querySelector(
        '[data-bs-dismiss="modal"]'
      ) as HTMLButtonElement | null;
      if (dismissButton) {
        dismissButton.click();
      }

      const bootstrap = (window as {
        bootstrap?: {
          Modal?: {
            getInstance: (el: Element) => { hide: () => void } | null;
            getOrCreateInstance?: (el: Element) => { hide: () => void };
          };
        };
      }).bootstrap;
      const modalInstance =
        bootstrap?.Modal &&
        (bootstrap.Modal.getInstance(modalElement) ||
          bootstrap.Modal.getOrCreateInstance?.(modalElement));
      modalInstance?.hide();

      modalElement.classList.remove("show");
      modalElement.setAttribute("aria-hidden", "true");
      modalElement.removeAttribute("aria-modal");
      modalElement.removeAttribute("role");
      (modalElement as HTMLElement).style.display = "none";
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
    };

    const handleSubmit = async () => {
      loading.value = true;
      Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

      try {
        if (props.supplier?.id) {
          await supplierService.updateSupplier(props.supplier.id, formData);
        } else {
          await supplierService.createSupplier(formData);
        }

        hideModal();
        resetForm();
        await nextTick();
        emit("saved");

        await Swal.fire({
          icon: "success",
          title: props.supplier?.id ? t("supplier_page.supplier_updated") : t("supplier_page.supplier_created"),
          text: t("supplier_page.supplier_saved_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error: unknown) {
        console.error("Failed to save supplier:", error);
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[] | string>; message?: string } };
        };

        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          Object.keys(errors).forEach((key) => {
            const fieldValue = errors[key];
            fieldErrors[key] = Array.isArray(fieldValue) ? String(fieldValue[0] || "") : String(fieldValue || "");
          });
        } else {
          await Swal.fire({
            icon: "error",
            title: t("supplier_page.save_failed"),
            text: axiosError.response?.data?.message || t("supplier_page.unable_to_save_supplier"),
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const generateSupplierCode = async () => {
      try {
        const response = await supplierService.getSuppliers({ per_page: 9999 });
        const supplierList = Array.isArray(response?.data) ? response.data : [];
        const nextNumber =
          supplierList
            .map((item) => {
              const match = String(item.code || "").match(/(\d+)\s*$/);
              return match ? Number(match[1]) : 0;
            })
            .reduce((max, current) => Math.max(max, current), 0) + 1;

        const prefix = stateStore.referencePrefixes.supplier || "SUP-";
        formData.code = `${prefix}${String(nextNumber).padStart(3, "0")}`;
      } catch (error) {
        console.error("Failed to generate supplier code:", error);
        await Swal.fire({
          icon: "error",
          title: t("supplier_page.generate_failed"),
          text: t("supplier_page.unable_to_generate_supplier_code"),
        });
      }
    };

    return {
      formData,
      loading,
      fieldErrors,
      handleSubmit,
      generateSupplierCode,
    };
  },
});
</script>

<style scoped>
.modal-content {
  border-radius: 20px;
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #e7ebf3;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  font-size: 12px;
  color: #98a2b3;
}

.reference-input-wrap {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.reference-input-wrap .form-control {
  flex: 1;
}

.modal-input {
  min-height: 52px;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  background: #fbfcfe;
  color: #1f2940;
  box-shadow: none;
  padding: 12px 16px;
}

.modal-input:focus {
  border-color: #5d5fef;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
}

.generate-code-btn {
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  min-height: 52px;
  padding-inline: 18px;
}
</style>
